import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, Trash2, MoreVertical, Package } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProductVariant } from "@/types/product-type";

interface VariantsTableProps {
  variants: ProductVariant[];
  onEdit: (variant: ProductVariant) => void;
  onDelete: (variant: ProductVariant) => void;
}

export function VariantsTable({
  variants,
  onEdit,
  onDelete,
}: VariantsTableProps) {
  const [isMobile, setIsMobile] = useState(false);

  // Responsive Detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
      <Table>
        <TableHeader className={isMobile ? "bg-slate-50" : ""}>
          <TableRow>
            {/* Image Column */}
            <TableHead className="w-[60px] px-2 text-[11px] uppercase font-bold">Img</TableHead>

            {/* Main Info Column */}
            <TableHead className="px-2 text-[11px] uppercase font-bold">
              {isMobile ? "Variant Details" : "Name"}
            </TableHead>

            {/* Desktop Only Columns */}
            {!isMobile && (
              <>
                <TableHead className="text-[11px] uppercase font-bold">SKU / Sale Code</TableHead>
                <TableHead className="text-[11px] uppercase font-bold">Variant Key</TableHead>
                <TableHead className="text-[11px] uppercase font-bold">Price</TableHead>
                <TableHead className="text-[11px] uppercase font-bold">Cost</TableHead>
              </>
            )}

            {/* Mobile Financial Summary */}
            {isMobile && (
              <TableHead className="text-right text-[11px] uppercase font-bold">Pricing</TableHead>
            )}

            <TableHead className="text-center w-[70px] text-[11px] uppercase font-bold">Stock</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {variants.length > 0 ? (
            variants.map((variant) => (
              <VariantRow
                key={variant.id}
                variant={variant}
                isMobile={isMobile}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={isMobile ? 5 : 8} className="h-32 text-center text-muted-foreground">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Package className="h-8 w-8 opacity-20" />
                  <p>No variants added yet.</p>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Sub-component for individual rows to keep logic clean
 */
function VariantRow({ variant, isMobile, onEdit, onDelete }: { variant: ProductVariant, isMobile: boolean, onEdit: (variant: ProductVariant) => void, onDelete: (variant: ProductVariant) => void }) {
  // Format currency
  const formatCur = (val: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

  return (
    <TableRow className="hover:bg-slate-50/50 transition-colors">
      {/* Thumbnail */}
      <TableCell className="px-2 py-3">
        <div className="w-10 h-10 rounded-md border bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {variant.imageUrl ? (
            <img src={variant.imageUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <Package className="h-5 w-5 text-slate-300" />
          )}
        </div>
      </TableCell>

      {/* Info: Name + (SKU/Key if Mobile) */}
      <TableCell className="px-2">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-sm font-semibold text-slate-900 truncate">
            {variant.name}
          </span>
          {isMobile && (
            <div className="flex flex-col">
              <span className="text-[10px] text-muted-foreground font-mono truncate">
                SKU: {variant.sku || 'N/A'}
              </span>
              <span className="text-[10px] text-indigo-600 font-medium italic">
                {variant.variantKey}
              </span>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {variant.color && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-violet-100 text-violet-700">
                    {variant.color}
                  </span>
                )}
                {variant.size && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-sky-100 text-sky-700">
                    {variant.size}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </TableCell>

      {/* Desktop Specific Data */}
      {!isMobile && (
        <>
          <TableCell className="text-sm font-mono">{variant.sku}</TableCell>
          <TableCell className="text-sm text-slate-600">
            <div className="flex flex-col gap-1">
              <span>{variant.variantKey}</span>
              <div className="flex flex-wrap gap-1">
                {variant.color && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700">
                    {variant.color}
                  </span>
                )}
                {variant.size && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-700">
                    {variant.size}
                  </span>
                )}
              </div>
            </div>
          </TableCell>
          <TableCell className="text-sm font-medium">{formatCur(variant.price!)}</TableCell>
          <TableCell className="text-sm text-slate-400">{formatCur(variant.cost!)}</TableCell>
        </>
      )}

      {/* Mobile Combined Pricing */}
      {isMobile && (
        <TableCell className="text-right">
          <div className="flex flex-col">
            <span className="text-sm font-bold text-slate-900">{formatCur(variant.price ?? 0)}</span>
            <span className="text-[10px] text-slate-400">Cost: {formatCur(variant.cost ?? 0)}</span>
          </div>
        </TableCell>
      )}

      {/* Stock - Color coded based on value */}
      <TableCell className="text-center">
        <Badge
          variant={variant.stock?.closingQty! <= 0 ? "destructive" : "secondary"}
          className={`px-2 py-0.5 text-[11px] font-bold ${variant.stock?.closingQty! > 0 && variant.stock?.closingQty! <= 5 ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : ""
            }`}
        >
          {variant.stock?.closingQty!}
        </Badge>
      </TableCell>

      {/* Actions */}
      <TableCell className="px-2 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(variant)}>
              <Edit2 className="mr-2 h-3.5 w-3.5" />
              Edit Variant
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(variant)}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}